import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  DepreciationService,
  RateLookupResult,
} from '@assetaccountant/core/services/depreciation.service';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import {
  HighlightedResult,
  InfiniteResult,
} from '@assetaccountant/core/models/azure-search.models';
import { TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

type LookupResult = InfiniteResult<{
  industries: string[];
  rates: string[];
  results: Array<HighlightedResult<RateLookupResult>>;
}> & { status: string | null; next(): void };

@Component({
  selector: 'app-depreciation-lookup-modal',
  templateUrl: './depreciation-lookup-modal.component.html',
  styleUrls: ['./depreciation-lookup-modal.component.scss'],
  standalone: false,
})
export class DepreciationLookupModalComponent implements OnInit {
  @Input() regimeId: number;

  @Output() selectRate = new EventEmitter<RateLookupResult>();

  form = this.fb.group({
    search: '',
    industry: this.fb.control(null as string | null),
  });

  private text$ = new Subject<string>();
  industries: Array<{
    name: string;
    value: string;
    children?: Array<{ name: string; value: string }>;
  }>;
  results$: Observable<LookupResult> = this.text$.pipe(
    map(search => ({ search, industry: this.form.value.industry })),
    tap(x => {
      this.status = x.search || x.industry != null ? 'SEARCHING' : 'NOTEXT';
    }),
    filter(x => !!x.search || x.industry != null),
    switchMap(({ search, industry }) =>
      this.depreciationService.lookupRate(search, this.regimeId, industry).pipe(
        map(x => ({
          ...x,
          status: x.next$ ? 'MORE' : null,
          next() {
            if (this.next$) {
              this.status = 'LOADING';
              this.next$?.subscribe(next => {
                this.results = this.results.concat(next.results);
                this.next$ = next.next$;
                this.status = next.next$ ? 'MORE' : null;
              });
            }
          },
        })),
        catchError(() => of({ industries: [], rates: [], results: [], status: null, next() {} })),
      ),
    ),
    tap(() => (this.status = 'DONE')),
  );

  status: 'NOTEXT' | 'SEARCHING' | 'DONE' = 'NOTEXT';

  constructor(
    public modal: NgbActiveModal,
    private fb: TypedFormBuilder,
    private depreciationService: DepreciationService,
  ) {}

  ngOnInit() {
    this.depreciationService.lookupIndustries(this.regimeId).subscribe(next => {
      this.industries = [];
      next.forEach(industry => {
        const names = industry.split(/ \/ /g);
        let parent = this.industries.find(i => i.name === names[0]);
        if (!parent) {
          parent = { name: names[0] || '(None)', value: names[0] };
          this.industries.push(parent);
        }
        if (names.length > 1) {
          parent.children = parent.children || [];
          parent.children.push({ name: names[1], value: industry });
        }
      });
    });
  }

  search() {
    this.text$.next(this.form.controls.search.value);
  }

  onSelect(result: RateLookupResult) {
    this.selectRate.emit(result);
    this.modal.close();
  }
}
